import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Dialog, DialogContent } from "../ui/dialog";
import { ChevronLeft, ChevronRight, MapPin, Star } from "lucide-react";

export const HotelGallery = ({ hotel, images, open, onOpenChange }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => emblaApi.off("select", onSel);
  }, [emblaApi]);

  if (!hotel) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-ocean-dark border-gold/30" data-testid="hotel-gallery-modal">
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {images.map((src, i) => (
                <div className="flex-[0_0_100%] min-w-0" key={i}>
                  <img src={src} alt={`${hotel.name} ${i + 1}`} className="w-full h-[300px] sm:h-[440px] object-cover" />
                </div>
              ))}
            </div>
          </div>
          <button onClick={scrollPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center transition-colors" data-testid="gallery-prev">
            <ChevronLeft className="w-5 h-5 text-ocean-dark" />
          </button>
          <button onClick={scrollNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center transition-colors" data-testid="gallery-next">
            <ChevronRight className="w-5 h-5 text-ocean-dark" />
          </button>
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ocean-dark to-transparent pointer-events-none" />
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-1.5 text-gold text-xs font-medium">
                <MapPin className="w-3.5 h-3.5" /> {hotel.location}
              </div>
              <h3 className="mt-1 font-serif text-2xl font-semibold text-white leading-tight">{hotel.name}</h3>
            </div>
            <span className="flex items-center gap-1 text-[11px] font-semibold text-ocean-dark bg-gold rounded-full px-2.5 py-1">
              <Star className="w-3 h-3 fill-ocean-dark" /> {hotel.stars}
            </span>
          </div>
          <div className="mt-4 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === selected ? "w-8 bg-gold" : "w-4 bg-white/30"}`}
                data-testid={`gallery-dot-${i}`}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
